// src/components/mitchRoom/modelOutputs/TeamResultsGrid.js
// Season × gameweek matrix: one row per club, one cell per fixture.
// Historical cells show W/D/L/CS percentages against the real result;
// future cells use the web-app FDR soft-colour by win probability.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { getTeamResultsGrid } from '../../../services/api';
import LoadingState from '../../shared/LoadingState';
import ExplanationModal from './ExplanationModal';
import FilterSelect from './FilterSelect';
import { getLogo } from './utils';
import { abbreviate, getWinColor, oppLabel, pct, softStyle } from './gridUtils';

const TEAM_COL = 150; // sticky first column width (px)

function cellTitle(cell, gw, clubName, oppName) {
  const ha = cell.home_away === 'H' ? 'vs' : '@';
  const lines = [`GW${gw} · ${clubName} ${ha} ${oppName} · ${cell.kickoff_datetime || ''}`];
  if (cell.percent_win != null) {
    lines.push(
      `Win ${pct(cell.percent_win)}% · Draw ${pct(cell.percent_draw)}% · ` +
        `Lose ${pct(cell.percent_lose)}% · CS ${pct(cell.percent_cs)}%`
    );
  } else {
    lines.push('No model prediction for this fixture');
  }
  if (cell.is_future) {
    lines.push('Upcoming fixture');
  } else if (cell.club_score != null) {
    lines.push(
      `Result: ${cell.club_score}-${cell.opp_club_score} (${cell.result || '-'})` +
        (cell.clean_sheet === 1 ? ' · Clean sheet' : '')
    );
  }
  return lines.join('\n');
}

/** W/D/L percentages with the realised outcome emphasised. */
function WdlLine({ cell }) {
  const parts = [
    { key: 'W', value: cell.percent_win },
    { key: 'D', value: cell.percent_draw },
    { key: 'L', value: cell.percent_lose },
  ];
  return (
    <span className="text-[9px] leading-tight tabular-nums text-neutral-500">
      {parts.map((p, i) => (
        <React.Fragment key={p.key}>
          {i > 0 && <span className="text-neutral-300"> · </span>}
          <span className={cn(cell.result === p.key && 'font-bold text-neutral-800')}>
            {pct(p.value)}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

function FixtureBox({ cell, oppShort }) {
  const label = oppLabel(oppShort, cell.home_away);

  if (cell.is_future) {
    return (
      <div
        className="flex w-full flex-col items-center gap-px rounded-token-sm px-1 py-0.5"
        style={softStyle(getWinColor(cell.percent_win))}
      >
        <span className="text-token-sm font-semibold leading-tight text-[#262626]">{label}</span>
        <span className="text-[10px] leading-tight tabular-nums text-[#262626]">
          W {pct(cell.percent_win)}%
        </span>
        <span className="text-[9px] leading-tight tabular-nums text-neutral-600">
          CS {pct(cell.percent_cs)}%
        </span>
      </div>
    );
  }

  const scoreColor =
    cell.result === 'W'
      ? 'text-emerald-700'
      : cell.result === 'L'
        ? 'text-red-600'
        : 'text-neutral-500';

  return (
    <div className="flex w-full flex-col items-center gap-px px-1 py-0.5">
      <span className="flex items-baseline gap-1 leading-tight">
        <span className="text-token-sm font-semibold">{label}</span>
        <span className={cn('text-token-sm font-semibold tabular-nums', scoreColor)}>
          {cell.club_score != null ? `${cell.club_score}-${cell.opp_club_score}` : '?'}
        </span>
      </span>
      {cell.percent_win != null ? (
        <>
          <WdlLine cell={cell} />
          <span className="text-[9px] leading-tight tabular-nums text-neutral-500">
            CS {pct(cell.percent_cs)}
            {cell.clean_sheet === 1 && <span className="text-emerald-600"> ✓</span>}
          </span>
        </>
      ) : (
        <span className="text-[9px] leading-tight text-neutral-300">no pred</span>
      )}
    </div>
  );
}

export default function TeamResultsGrid({ teamMap, refreshKey }) {
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null); // null = server default (newest)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTeamResultsGrid(season)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching team results grid:', err);
        setError(err.message || 'Failed to load team results grid');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [season, refreshKey, attempt]);

  const seasonOptions = useMemo(
    () => (data?.seasons || []).map((s) => ({ id: s.compSeasonID, label: s.label })),
    [data?.seasons]
  );

  const oppShortName = useCallback(
    (cell) => teamMap?.get(cell.opp_club_optaID)?.short_name || abbreviate(cell.opp_club),
    [teamMap]
  );

  const stats = useMemo(() => {
    if (!data) return null;
    let fixtures = 0;
    let upcoming = 0;
    data.rows.forEach((row) => {
      Object.values(row.cells).forEach((list) => {
        list.forEach((cell) => {
          fixtures += 1;
          if (cell.is_future) upcoming += 1;
        });
      });
    });
    return { fixtures, upcoming };
  }, [data]);

  if (loading && !data) {
    return <LoadingState message="Loading team results grid..." minHeight={200} />;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertTitle>Error loading team results</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => setAttempt((a) => a + 1)}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="p-6 text-center text-token-md text-muted-foreground">
        No team predictions available.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-4">
        <FilterSelect
          label="Season"
          value={season ?? data.compSeasonID}
          options={seasonOptions}
          onChange={(v) => setSeason(v)}
          className="h-9 w-[140px]"
        />
        {/* Legend — literal copies of the real chips */}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-token-base text-neutral-500">
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-3 w-3 rounded-[2px]"
              style={softStyle(getWinColor(60))}
            />
            projected (colour = win%)
          </span>
          <span className="flex items-center gap-1">
            <span className="font-semibold text-emerald-700">2-0</span>/
            <span className="font-semibold text-red-600">0-2</span> result
          </span>
          <span className="flex items-center gap-1">
            <span className="font-bold text-neutral-800">46</span>·27·27 realised outcome
          </span>
          <span>
            CS <span className="text-emerald-600">✓</span> clean sheet kept
          </span>
          <span>
            <span className="font-semibold">ARS</span> home · <span>ars</span> away
          </span>
        </div>
      </div>

      {stats && (
        <p className="mb-2 text-token-base text-muted-foreground">
          {data.rows.length} clubs · {stats.fixtures} fixtures ({stats.upcoming} upcoming) · click
          a fixture for its explanation (log-λ terms or SHAP)
        </p>
      )}

      <div
        className={cn(
          'max-h-[70dvh] min-h-[280px] overflow-auto rounded-token-large border border-borderGrey bg-white',
          loading && 'opacity-60'
        )}
      >
        <table className="w-full border-separate border-spacing-0 text-token-base">
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-[4] border-b border-r border-borderGrey bg-white p-2 text-left font-semibold"
                style={{ minWidth: TEAM_COL, width: TEAM_COL }}
              >
                Team
              </th>
              {data.gameweeks.map((gw) => (
                <th
                  key={gw}
                  className="sticky top-0 z-sticky-header w-[80px] min-w-[80px] border-b border-borderGrey bg-white px-0 py-1 text-center font-semibold"
                >
                  GW{gw}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const team = teamMap?.get(row.club_optaID);
              const displayName = team?.short_name || abbreviate(row.club);
              const logo = getLogo(team?.name || row.club);
              return (
                <tr key={row.club_optaID} className="group border-b border-borderGrey">
                  <td
                    className="sticky left-0 z-sticky-column border-b border-r border-borderGrey bg-white px-2 py-1 group-hover:bg-neutral-50"
                    style={{ minWidth: TEAM_COL, width: TEAM_COL }}
                  >
                    <div className="flex items-center gap-2">
                      {logo && (
                        <img src={logo} alt={row.club} className="h-5 w-5 object-contain" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-medium leading-tight">{displayName}</p>
                        <p className="truncate text-token-xs leading-tight text-muted-foreground">
                          {row.club}
                        </p>
                      </div>
                    </div>
                  </td>
                  {data.gameweeks.map((gw) => {
                    const list = row.cells[String(gw)];
                    if (!list || list.length === 0) {
                      return (
                        <td
                          key={gw}
                          className="border-b border-borderGrey bg-white px-0 py-1 text-center align-middle group-hover:bg-neutral-50"
                        >
                          <span className="select-none text-neutral-300">–</span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={gw}
                        className="border-b border-borderGrey bg-white px-0.5 py-1 align-middle group-hover:bg-neutral-50"
                      >
                        <div className="flex flex-col gap-0.5">
                          {list.map((cell) => {
                            const oppShort = oppShortName(cell);
                            return (
                              <button
                                key={cell.fixture_optaID}
                                type="button"
                                className={cn(
                                  'w-full cursor-pointer rounded-token-sm',
                                  cell.percent_win != null && 'hover:ring-1 hover:ring-neutral-300'
                                )}
                                title={cellTitle(cell, gw, row.club, cell.opp_club || oppShort)}
                                onClick={() =>
                                  cell.percent_win != null &&
                                  setModal({
                                    entityId: row.club_optaID,
                                    fixtureId: cell.fixture_optaID,
                                    entityName: displayName,
                                    opponent: oppShort,
                                  })
                                }
                              >
                                <FixtureBox cell={cell} oppShort={oppShort} />
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Explanation Modal (v2 log-λ terms or legacy SHAP) */}
      <ExplanationModal
        open={modal != null}
        onClose={() => setModal(null)}
        modelType="team"
        entityId={modal?.entityId}
        fixtureId={modal?.fixtureId}
        contextInfo={{ entityName: modal?.entityName, opponent: modal?.opponent }}
      />
    </div>
  );
}
